import { useMemo } from 'react';
import { useAppData } from '../context/AppDataContext';
import { useAuth } from '../context/AuthContext';

const isAppAdminUser = (user) => {
  const userType = user?.userType || user?.user_type;
  return userType === 1 || userType === '1' || userType === 'AppAdmin' || userType === 'appAdmin';
};

export const usePermissions = (storeId) => {
  const {
    currentStore,
    getStorePermissions,
    isStoreOwner,
    permissionsLoading,
    permissionsLoaded,
    storesLoading,
    storesLoaded,
    storesError,
    permissionsError,
    refreshPermissions,
    isStoreSelectionHydrated,
    stores,
  } = useAppData();
  const { isManagementAuthenticated, managementUser } = useAuth();

  const isAppAdmin = isAppAdminUser(managementUser);

  const storedStoreId = useMemo(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    const id = localStorage.getItem('currentStoreId');
    if (!id) {
      return null;
    }

    return stores.some((store) => store.id === id) ? id : null;
  }, [stores]);

  const targetStoreId = storeId || currentStore?.id || storedStoreId;

  const loading = useMemo(() => {
    if (!isManagementAuthenticated || !managementUser || isAppAdmin) {
      return false;
    }

    if (permissionsLoading || storesLoading) {
      return true;
    }

    if (!storesLoaded) {
      return !storesError;
    }

    if (stores.length > 0 && !isStoreSelectionHydrated) {
      return true;
    }

    if (!permissionsLoaded) {
      return true;
    }

    return false;
  }, [
    isManagementAuthenticated,
    managementUser,
    isAppAdmin,
    permissionsLoading,
    storesLoading,
    storesLoaded,
    storesError,
    permissionsLoaded,
    isStoreSelectionHydrated,
    stores.length,
  ]);

  const permissions = useMemo(() => {
    if (isAppAdmin) {
      return new Set(['*']);
    }

    if (!targetStoreId) {
      return new Set();
    }

    return getStorePermissions(targetStoreId);
  }, [isAppAdmin, targetStoreId, getStorePermissions]);

  const ownerAccess = useMemo(() => {
    if (!targetStoreId) {
      return false;
    }

    return isStoreOwner(targetStoreId);
  }, [targetStoreId, isStoreOwner]);

  const hasPermission = useMemo(() => {
    return (permission) => {
      if (!permission) return false;
      
      // App admins have all permissions
      if (permissions.has('*')) return true;
      
      return permissions.has(permission);
    };
  }, [permissions]);

  const hasAnyPermission = useMemo(() => {
    return (permissionList) => {
      if (!permissionList || permissionList.length === 0) return false;
      
      // App admins have all permissions
      if (permissions.has('*')) return true;
      
      return permissionList.some(permission => permissions.has(permission));
    };
  }, [permissions]);

  const hasAllPermissions = useMemo(() => {
    return (permissionList) => {
      if (!permissionList || permissionList.length === 0) return false;
      
      // App admins have all permissions
      if (permissions.has('*')) return true;
      
      return permissionList.every(permission => permissions.has(permission));
    };
  }, [permissions]);

  return {
    permissions,
    isOwner: ownerAccess,
    loading,
    error: permissionsError,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions
  };
};

export default usePermissions;

